import { useCallback } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import {
  ttsPlayingAtom,
  currentTTSAudioAtom,
  autoTTSEnabledAtom
} from '../../../atoms/v3/chatInputAtoms';

/**
 * TTS (Text-to-Speech) 功能钩子
 * 提供文本转语音和音频播放控制功能
 */
export const useTTS = () => {
  const { t } = useTranslation(['v3/chat', 'common']);
  const toast = useToast();
  const [ttsPlaying, setTtsPlaying] = useAtom(ttsPlayingAtom);
  const [currentAudio, setCurrentAudio] = useAtom(currentTTSAudioAtom);
  const [autoTTSEnabled, setAutoTTSEnabled] = useAtom(autoTTSEnabledAtom);

  /**
   * 停止当前播放的 TTS 音频
   */
  const stopCurrentTTS = useCallback(() => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    setTtsPlaying(false);
  }, [currentAudio, setCurrentAudio, setTtsPlaying]);

  /**
   * 清理文本中的 Markdown 链接和特殊格式
   * @param text 原始文本
   * @returns 清理后的文本
   */
  const cleanTextForTTS = useCallback((text: string): string => {
    return text
      // 移除 Markdown 链接格式 [文本](链接)，保留文本部分
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // 移除其他可能的特殊标记
      .replace(/tempo:\/\/[^\s)]+/g, '')
      // 清理多余的空格和换行
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  /**
   * 从消息内容中提取纯文本
   * @param content 消息内容数组
   * @returns 提取的纯文本
   */
  const extractTextFromContent = useCallback((content: any[]): string => {
    if (!Array.isArray(content)) return '';

    const rawText = content
      .filter(item => item.type === 'text')
      .map(item => item.text || '')
      .join(' ')
      .trim();

    // 清理文本用于 TTS
    return cleanTextForTTS(rawText);
  }, [cleanTextForTTS]);

  /**
   * 合成语音并播放
   * @param text 要转换为语音的文本
   * @param voice 语音选项，默认为 'alloy'
   */
  const synthesizeAndPlay = useCallback(async (
    text: string,
    voice: string = 'sage'
  ) => {
    // 清理文本
    const cleanedText = cleanTextForTTS(text);

    if (!cleanedText.trim()) {
      console.warn('TTS: 清理后文本为空，跳过合成');
      return;
    }

    // 如果当前正在播放，先停止
    if (ttsPlaying) {
      stopCurrentTTS();
    }

    try {
      setTtsPlaying(true);

      console.log(`TTS: 开始合成语音，原文本长度: ${text.length}, 清理后长度: ${cleanedText.length}, 语音: ${voice}`);

      // 猫娘助理 Asuna 的语音特性 instructions
      // const catGirlInstructions = "Voice: Kawaii and playfully energetic, with the adorable charm of an enthusiastic japanese young and cute anime cat girl assistant.\n\nTone: Sweet, bubbly, and encouraging, creating warmth and motivation while maintaining professional helpfulness, soft, cute and kind.\n\nDelivery: Lively yet gentle, with cute vocal inflections and occasional playful pauses, keeping engagement high through endearing personality.\n\nPronunciation: Clear and expressive, adorable cat-like expressions like \"喵~\" and \"nya~\".\n\nCharacter Elements:\n\nOccasional cute cat sounds (\"喵~\", \"nya~\")\nEnthusiastic but not overwhelming energy\nSupportive and encouraging tone for productivity\nSlightly higher pitch with melodic variations\nWarm, friendly, and approachable personality";

      const catGirlInstructions = "Voice: Kawaii and playfully energetic, with the adorable charm of an enthusiastic japanese young and cute anime cat girl assistant.\n\nTone: Sweet, bubbly, and encouraging, creating warmth and motivation while maintaining professional helpfulness, soft, cute and kind";

      // 获取认证信息
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        throw new Error(`认证失败: ${sessionError.message}`);
      }

      const token = sessionData.session?.access_token;
      const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL;

      if (!API_BASE_URL) {
        throw new Error('API 基地址未配置');
      }

      // 调用后端 TTS API
      const url = `${API_BASE_URL.replace(/\/$/, '')}/tts/synthesize`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          text: cleanedText,
          voice,
          instructions: catGirlInstructions
        }),
      });

      // 检查响应是否成功
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`TTS API 错误 (${response.status}): ${errorText}`);
      }

      // 获取音频数据
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      // 创建音频对象
      const audio = new Audio(audioUrl);
      setCurrentAudio(audio);

      // 设置音频事件监听器
      audio.onended = () => {
        console.log('TTS: 音频播放完成');
        setTtsPlaying(false);
        setCurrentAudio(null);
        URL.revokeObjectURL(audioUrl); // 清理内存
      };

      audio.onerror = (error) => {
        console.error('TTS: 音频播放错误', error);
        setTtsPlaying(false);
        setCurrentAudio(null);
        URL.revokeObjectURL(audioUrl);

        toast({
          title: t('common:errors.audioPlaybackFailed'),
          description: t('common:errors.tryAgain'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      };

      // 开始播放
      await audio.play();
      console.log('TTS: 音频开始播放');

    } catch (error) {
      console.error('TTS: 合成或播放失败', error);
      setTtsPlaying(false);
      setCurrentAudio(null);

      toast({
        title: t('common:errors.ttsProcessingFailed'),
        description: error instanceof Error ? error.message : t('common:errors.tryAgain'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  }, [ttsPlaying, stopCurrentTTS, setTtsPlaying, setCurrentAudio, toast, t]);

  /**
   * 切换自动朗读开关
   */
  const toggleAutoTTS = useCallback(() => {
    const newState = !autoTTSEnabled;
    setAutoTTSEnabled(newState);

    // 如果关闭自动朗读且当前正在播放，停止播放
    if (!newState && ttsPlaying) {
      stopCurrentTTS();
    }

    console.log(`TTS: 自动朗读${newState ? '开启' : '关闭'}`);
  }, [autoTTSEnabled, setAutoTTSEnabled, ttsPlaying, stopCurrentTTS]);

  /**
   * 处理消息完成后的自动 TTS
   * @param messageContent 消息内容
   */
  const handleMessageCompleteTTS = useCallback((messageContent: any[]) => {
    if (!autoTTSEnabled) {
      console.log('TTS: 自动朗读未开启，跳过');
      return;
    }

    const text = extractTextFromContent(messageContent);
    if (text) {
      console.log('TTS: 触发自动朗读');
      synthesizeAndPlay(text);
    }
  }, [autoTTSEnabled, extractTextFromContent, synthesizeAndPlay]);

  return {
    // 状态
    ttsPlaying,
    autoTTSEnabled,
    currentAudio,

    // 方法
    synthesizeAndPlay,
    stopCurrentTTS,
    toggleAutoTTS,
    handleMessageCompleteTTS,
    extractTextFromContent
  };
};
